import { Injectable, inject } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Analytics, getAnalytics, logEvent, setUserId, setUserProperties, isSupported } from 'firebase/analytics';
import { FirebaseApp } from 'firebase/app';
import { filter } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

/**
 * Firebase Analytics service for tracking user behavior and events.
 * 
 * This service initializes Firebase Analytics and provides methods for:
 * - Automatic page view tracking
 * - Custom event logging
 * - User identification
 * - User properties
 */
@Injectable({ providedIn: 'root' })
export class FirebaseAnalyticsService {
    private analytics: Analytics | null = null;
    private initialized = false;
    private router = inject(Router);

    /**
     * Initialize Firebase Analytics with the given Firebase app instance.
     * Should be called after Firebase app is initialized.
     */
    async initialize(app: FirebaseApp, useEmulator: boolean = false): Promise<void> {
        // Don't initialize analytics in emulator mode or if already initialized
        if (useEmulator || this.initialized) {
            if (useEmulator) {
                console.log('Firebase Analytics disabled in emulator mode');
            }
            return;
        }

        try {
            // Check if analytics is supported in this browser
            const supported = await isSupported();
            if (!supported) {
                console.log('Firebase Analytics not supported in this browser');
                return;
            }

            this.analytics = getAnalytics(app);
            this.initialized = true;

            // Set up automatic page view tracking
            this.setupPageViewTracking();

            console.log('Firebase Analytics initialized successfully');
        } catch (error) {
            console.error('Failed to initialize Firebase Analytics:', error);
        }
    }

    /**
     * Track page views automatically when route changes.
     */
    private setupPageViewTracking(): void {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                this.logPageView(event.urlAfterRedirects);
            });
    }

    /**
     * Log a page view event.
     */
    logPageView(pagePath: string, pageTitle?: string): void {
        if (!this.analytics) return;

        logEvent(this.analytics, 'page_view', {
            page_path: pagePath,
            page_title: pageTitle || document.title,
            page_location: window.location.href
        });
    }

    /**
     * Log a custom event.
     */
    logEvent(eventName: string, eventParams?: Record<string, any>): void {
        if (!this.analytics) return;

        logEvent(this.analytics, eventName, eventParams);
    }

    /**
     * Set the user ID for analytics tracking.
     * Call this after user logs in.
     */
    setUserId(userId: string | null): void {
        if (!this.analytics) return;

        setUserId(this.analytics, userId);
    }

    /**
     * Set user properties for segmentation.
     */
    setUserProperties(properties: Record<string, string>): void {
        if (!this.analytics) return;

        setUserProperties(this.analytics, properties);
    }

    // ========================================
    // Pre-defined event helpers
    // ========================================

    /**
     * Log a login event.
     */
    logLogin(method: 'email' | 'email_link' | 'google' | 'microsoft' | 'saml'): void {
        this.logEvent('login', { method });
    }

    /**
     * Log a sign up event.
     */
    logSignUp(method: 'email' | 'email_link' | 'google' | 'microsoft' | 'saml'): void {
        this.logEvent('sign_up', { method });
    }

    /**
     * Log when user views a device.
     */
    logDeviceView(deviceId: string, deviceName?: string): void {
        this.logEvent('view_device', {
            device_id: deviceId,
            device_name: deviceName
        });
    }

    /**
     * Log when user performs a device action.
     */
    logDeviceAction(actionType: string, deviceId: string): void {
        this.logEvent('device_action', {
            action_type: actionType,
            device_id: deviceId
        });
    }

    /**
     * Log when user views a namespace.
     */
    logNamespaceView(namespaceId: string, namespaceName?: string): void {
        this.logEvent('view_namespace', {
            namespace_id: namespaceId,
            namespace_name: namespaceName
        });
    }

    /**
     * Log when user creates something.
     */
    logCreate(itemType: 'device' | 'namespace' | 'api_key' | 'user', itemId?: string): void {
        this.logEvent('create_item', {
            item_type: itemType,
            item_id: itemId
        });
    }

    /**
     * Log search events.
     */
    logSearch(searchTerm: string, resultsCount?: number): void {
        this.logEvent('search', {
            search_term: searchTerm,
            results_count: resultsCount
        });
    }

    /**
     * Log errors for debugging.
     */
    logError(errorType: string, errorMessage: string, context?: string): void {
        this.logEvent('app_error', {
            error_type: errorType,
            error_message: errorMessage.substring(0, 100), // Limit length
            context: context
        });
    }
}
